import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Card, CardMedia, CardContent, Button, Grid, CircularProgress, TextField, Chip, Autocomplete, MenuItem, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Pagination, Select, FormControl, InputLabel } from '@mui/material';
import { db } from '../services/firebase';
import { collection, getDocs, addDoc, query, orderBy, doc, updateDoc, deleteDoc, setDoc } from 'firebase/firestore';
import usePermissions from '../hooks/usePermissions';
import { useTheme } from '@mui/material/styles';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import * as XLSX from 'xlsx';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Collapse from '@mui/material/Collapse';
import useMediaQuery from '@mui/material/useMediaQuery';
import CloseIcon from '@mui/icons-material/Close';

export default function BookRecommendations() {
  const [livros, setLivros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');
  // Novo estado para busca por palavras-chave na descrição
  const [buscaDescricao, setBuscaDescricao] = useState('');
  // Estado para o formulário de cadastro
  const [novoLivro, setNovoLivro] = useState({
    titulo: '',
    autores: [''],
    categorias: [],
    descricao: '',
    link: '',
    imagem: ''
  });
  const [enviando, setEnviando] = useState(false);
  const { isAdmin } = usePermissions();
  const theme = useTheme();

  // Filtros avançados
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroAutor, setFiltroAutor] = useState('');
  const [editandoId, setEditandoId] = useState(null);
  const [editLivro, setEditLivro] = useState({});

  const [categorias, setCategorias] = useState([]);

  // Mover a definição de autoresUnicos para fora do render, após buscarLivros
  const [autoresUnicos, setAutoresUnicos] = useState([]);

  const [openAjuda, setOpenAjuda] = useState(false);
  // Estado para modal de detalhes do livro
  const [livroSelecionado, setLivroSelecionado] = useState(null);
  const [livrosRecomendados, setLivrosRecomendados] = useState([]);
  // Estado para saber se o usuário clicou em Buscar
  const [buscou, setBuscou] = useState(false);

  const descricaoEditRef = useRef(null);

  // Novo estado para categorias sugeridas
  const [categoriasSugeridas, setCategoriasSugeridas] = useState([]);

  // Estados para paginação
  const [pagina, setPagina] = useState(1);
  const [livrosPorPagina, setLivrosPorPagina] = useState(20);

  // Livros paginados
  const livrosFiltrados = livros; // Aqui você pode aplicar filtros se quiser
  const totalLivros = livrosFiltrados.length;
  const mostrarTodos = livrosPorPagina === -1;
  const totalPaginas = mostrarTodos ? 1 : Math.ceil(totalLivros / livrosPorPagina);
  const livrosPaginados = mostrarTodos ? livrosFiltrados : livrosFiltrados.slice((pagina - 1) * livrosPorPagina, pagina * livrosPorPagina);

  // Novo estado para controlar a limpeza de filtros
  const [limpouFiltro, setLimpouFiltro] = useState(false);

  // Buscar livros do Firestore (com filtros avançados)
  const buscarLivros = async () => {
    setLoading(true);
    setBuscou(true);
    const q = query(collection(db, 'livros'), orderBy('titulo'));
    const snapshot = await getDocs(q);
    let todosLivros = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    // Filtros
    if (busca.trim()) {
      const termo = busca.trim().toLowerCase();
      todosLivros = todosLivros.filter(livro =>
        livro.titulo?.toLowerCase().includes(termo)
      );
    }
    if (buscaDescricao.trim()) {
      const termoDesc = buscaDescricao.trim().toLowerCase();
      todosLivros = todosLivros.filter(livro =>
        livro.descricao?.toLowerCase().includes(termoDesc)
      );
    }
    if (filtroCategoria) {
      todosLivros = todosLivros.filter(livro => livro.categorias?.includes(filtroCategoria));
    }
    if (filtroAutor) {
      todosLivros = todosLivros.filter(livro => livro.autores?.includes(filtroAutor));
    }
    setLivros(todosLivros);
    setLoading(false);
  };

  // Buscar categorias do Firestore
  const buscarCategorias = async () => {
    const snapshot = await getDocs(collection(db, 'categorias'));
    setCategorias(snapshot.docs.map(doc => doc.data().nome));
  };

  useEffect(() => {
    buscarCategorias();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // Atualiza autores únicos sempre que livros mudar
    setAutoresUnicos(Array.from(new Set(livros.map(l => l.autores).flat().filter(Boolean))));
  }, [livros]);

  useEffect(() => {
    if (editandoId && descricaoEditRef.current) {
      descricaoEditRef.current.focus();
    }
  }, [editandoId]);

  // Função para lidar com mudanças nos campos do formulário
  const handleChange = (e) => {
    setNovoLivro({ ...novoLivro, [e.target.name]: e.target.value });
  };

  // Função para adicionar nova categoria
  const adicionarCategoria = async (nome) => {
    if (!nome || categorias.includes(nome)) return;
    await addDoc(collection(db, 'categorias'), { nome });
    setCategorias([...categorias, nome]);
  };

  // Função para adicionar novo autor
  const adicionarAutor = () => {
    setNovoLivro({ ...novoLivro, autores: [...novoLivro.autores, ''] });
  };

  // Função para remover autor
  const removerAutor = (index) => {
    const novosAutores = novoLivro.autores.filter((_, i) => i !== index);
    setNovoLivro({ ...novoLivro, autores: novosAutores });
  };

  // Função para adicionar nova categoria
  const adicionarEditAutor = () => {
    setEditLivro({ ...editLivro, autores: [...(editLivro.autores || []), ''] });
  };

  // Função para remover autor
  const removerEditAutor = (index) => {
    const novosAutores = (editLivro.autores || []).filter((_, i) => i !== index);
    setEditLivro({ ...editLivro, autores: novosAutores });
  };

  // Função utilitária para gerar slug
  function slugify(str) {
    return str
      .toString()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '') // remove acentos
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-') // troca tudo que não for letra/número por hífen
      .replace(/^-+|-+$/g, ''); // remove hífens do início/fim
  }

  // Função para cadastrar novo livro (apenas admin)
  const cadastrarLivro = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    setEnviando(true);
    const slug = slugify(novoLivro.titulo);
    // Verifica se já existe livro com esse slug
    const docRef = doc(db, 'livros', slug);
    const docSnap = await getDocs(query(collection(db, 'livros')));
    const exists = docSnap.docs.some(d => d.id === slug);
    if (exists) {
      alert('Já existe um livro com esse título!');
      setEnviando(false);
      return;
    }
    await setDoc(docRef, { ...novoLivro });
    setEnviando(false);
    setNovoLivro({ titulo: '', autores: [''], categorias: [], descricao: '', link: '', imagem: '' });
    setCategoriasSugeridas([]);
    buscarLivros();
  };

  // Função para iniciar edição
  const iniciarEdicao = (livro) => {
    setEditandoId(livro.id);
    setEditLivro({
      ...livro,
      autores: Array.isArray(livro.autores)
        ? livro.autores
        : livro.autor
          ? [livro.autor]
          : ['']
    });
  };

  // Função para salvar edição
  const salvarEdicao = async () => {
    if (!isAdmin) return;
    await updateDoc(doc(db, 'livros', editandoId), editLivro);
    setEditandoId(null);
    setEditLivro({});
    buscarLivros();
  };

  // Função para cancelar edição
  const cancelarEdicao = () => {
    setEditandoId(null);
    setEditLivro({});
  };

  // Função para remover livro
  const removerLivro = async (id) => {
    if (!isAdmin) return;
    await deleteDoc(doc(db, 'livros', id));
    buscarLivros();
  };

  // Listas únicas para filtros
  const categoriasUnicas = Array.from(new Set(livros.map(l => l.categorias).flat().filter(Boolean)));

  // Função para atualizar autor
  const handleAutorChange = (index, value) => {
    const novosAutores = [...novoLivro.autores];
    novosAutores[index] = value;
    setNovoLivro({ ...novoLivro, autores: novosAutores });
  };

  // Função para atualizar autor na edição
  const handleEditAutorChange = (index, value) => {
    const novosAutores = [...editLivro.autores];
    novosAutores[index] = value;
    setEditLivro({ ...editLivro, autores: novosAutores });
  };

  // Função para abrir modal e calcular recomendações
  const abrirDetalhesLivro = (livro) => {
    setLivroSelecionado(livro);
    // Recomendações: livros com categorias ou autores em comum, exceto o próprio
    const recomendados = livros.filter(l =>
      l.id !== livro.id && (
        l.categorias?.some(cat => livro.categorias?.includes(cat)) ||
        l.autores?.some(autor => livro.autores?.includes(autor))
      )
    ).slice(0, 8);
    setLivrosRecomendados(recomendados);
  };
  const fecharDetalhesLivro = () => {
    setLivroSelecionado(null);
    setLivrosRecomendados([]);
  };

  // Função para exportar para Excel
  const handleExportExcel = () => {
    // Monta os dados para exportação
    const data = livros.map(livro => {
      const categorias = Array.isArray(livro.categorias) ? livro.categorias : [];
      return {
        'Título': livro.titulo || '',
        'Autor': (Array.isArray(livro.autores) ? livro.autores.join(', ') : (livro.autores || '')),
        'Descrição': livro.descricao || '',
        'Link Amazon': livro.link || '',
        'Link Capa': livro.imagem || '',
        'Categoria 1': categorias[0] || '',
        'Categoria 2': categorias[1] || '',
        'Categoria 3': categorias[2] || '',
        'Categoria 4': categorias[3] || '',
        'Categoria 5': categorias[4] || '',
      };
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Livros');
    XLSX.writeFile(wb, 'livros_habitop.xlsx');
  };

  // Função para sugerir categorias (versão mais inteligente)
  const sugerirCategorias = () => {
    const texto = [
      novoLivro.titulo,
      (novoLivro.autores || []).join(' '),
      novoLivro.descricao
    ].join(' ').toLowerCase();

    // Divide o texto em palavras únicas
    const palavras = Array.from(new Set(texto.split(/\W+/).filter(Boolean)));

    // Para cada categoria, conta quantas palavras da categoria aparecem no texto
    const scores = categorias.map(cat => {
      const catPalavras = cat.toLowerCase().split(/\W+/).filter(Boolean);
      // Conta quantas palavras da categoria aparecem no texto
      const matchCount = catPalavras.reduce((acc, palavra) => palavras.includes(palavra) ? acc + 1 : acc, 0);
      return { cat, matchCount };
    }).filter(s => s.matchCount > 0);

    // Ordena por maior matchCount e pega até 5
    const sugeridas = scores
      .sort((a, b) => b.matchCount - a.matchCount)
      .slice(0, 5)
      .map(s => s.cat);

    // Se não houver nenhuma correspondência, sugere as 5 primeiras categorias existentes
    if (sugeridas.length === 0) {
      setCategoriasSugeridas(categorias.slice(0, 5));
    } else {
      setCategoriasSugeridas(sugeridas);
    }
  };

  // Função para limpar filtros e esconder título
  const limparFiltros = () => {
    setBusca('');
    setBuscaDescricao('');
    setFiltroCategoria('');
    setFiltroAutor('');
    setBuscou(false);
    setLimpouFiltro(true); // Dispara o efeito de busca limpa
  };

  useEffect(() => {
    if (limpouFiltro) {
      buscarLivros();
      setLimpouFiltro(false);
    }
    // eslint-disable-next-line
  }, [limpouFiltro]);

  const [buscaAberta, setBuscaAberta] = useState(true);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Sempre abrir com filtro aberto e todos os livros ao montar
  useEffect(() => {
    setBusca('');
    setBuscaDescricao('');
    setFiltroCategoria('');
    setFiltroAutor('');
    setBuscou(false);
    setBuscaAberta(true);
    buscarLivros();
    // eslint-disable-next-line
  }, []);

  return (
    <Box>
      {/* Filtros avançados: sticky, sempre no topo do conteúdo */}
      <Box
        sx={{
          position: 'sticky',
          top: { xs: 56, sm: 64 },
          zIndex: 1200,
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
          boxShadow: 1, // Sombra mais forte para destacar
        }}
      >
        <Collapse in={!isMobile || buscaAberta} timeout="auto" unmountOnExit>
          <Box
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 2,
              borderRadius: 0,
              mb: 0,
              p: 1,
              mt: { xs: 0.5, sm: 1 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2
            }}
          >
            <TextField
              label="Buscar título"
              value={busca}
              onChange={e => setBusca(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); buscarLivros(); } }}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, height: 40, '& .MuiInputBase-root': { height: 40 }, minWidth: { xs: 180, sm: 240, md: 280 }, maxWidth: 340, mr: { sm: 2, md: 3 } }}
              fullWidth
            />
            <TextField
              label="Buscar por palavra-chave"
              value={buscaDescricao}
              onChange={e => setBuscaDescricao(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); buscarLivros(); } }}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1, height: 40, '& .MuiInputBase-root': { height: 40 }, minWidth: { xs: 180, sm: 220, md: 260 }, maxWidth: 320, mr: { sm: 2, md: 3 } }}
              fullWidth
            />
            <TextField
              select
              label="Categoria"
              value={filtroCategoria}
              onChange={e => setFiltroCategoria(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180, height: 40, '& .MuiInputBase-root': { height: 40, alignItems: 'center' }, '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: '40px', paddingTop: 0, paddingBottom: 0 } }}
              fullWidth
            >
              {[...categoriasUnicas].sort((a, b) => a.localeCompare(b, 'pt-BR')).map(cat => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Autor"
              value={filtroAutor}
              onChange={e => setFiltroAutor(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180, height: 40, '& .MuiInputBase-root': { height: 40, alignItems: 'center' }, '& .MuiSelect-select': { display: 'flex', alignItems: 'center', height: '40px', paddingTop: 0, paddingBottom: 0 } }}
              fullWidth
            >
              {[...autoresUnicos].sort((a, b) => a.localeCompare(b, 'pt-BR')).map(autor => (
                <MenuItem key={autor} value={autor}>{autor}</MenuItem>
              ))}
            </TextField>
            <Button
              onClick={buscarLivros}
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ minWidth: 120, width: { xs: '100%', sm: 'auto' }, height: 40, p: 0, fontWeight: 600, fontSize: '1rem', boxSizing: 'border-box' }}
              fullWidth={true}
            >
              {loading ? <CircularProgress size={22} /> : 'Buscar'}
            </Button>
          </Box>
        </Collapse>
      </Box>
      {/* Ícone de ajuda e exportar só em telas maiores, agora fora do título */}
      {!isMobile && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'center', sm: 'flex-end' }, mb: 2 }}>
          {isAdmin && (
            <Button onClick={handleExportExcel} variant="outlined" color="primary" sx={{ minWidth: 160, width: { xs: '100%', sm: 'auto' } }}>
              Exportar para Excel
            </Button>
          )}
          <IconButton onClick={() => setOpenAjuda(true)} color="primary" size="large">
            <HelpOutlineIcon />
          </IconButton>
        </Box>
      )}
      <Dialog open={openAjuda} onClose={() => setOpenAjuda(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2, color: theme.palette.primary.main }}>
          Como usar as Recomendações de Livros
          <IconButton
            aria-label="fechar"
            onClick={() => setOpenAjuda(false)}
            edge="end"
            sx={{ ml: 2 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom sx={{ mb: 2 }}>
            Todos os livros recomendados aqui no Habitop são cuidadosamente selecionados para promover o desenvolvimento pessoal e ajudar você a evoluir em várias áreas importantes da vida. São obras voltadas para autoconhecimento, habilidades, mentalidade, hábitos e crescimento em diversos aspectos.
          </Typography>
          <Typography gutterBottom sx={{ mb: 2 }}>
            Aqui você encontra sugestões de livros para seu desenvolvimento pessoal e profissional.
          </Typography>
          <Typography gutterBottom sx={{ mb: 2 }}>
            <b>Dica:</b> Clique em um livro (na capa) para ler a descrição completa, ver mais informações e acessar sugestões de outros livros relevantes! O sistema utiliza um algoritmo especial para recomendar livros que tenham relação com o que você está visualizando, considerando autores e categorias semelhantes.
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 500, mt: 2, color: theme.palette.primary.main }}>Como pesquisar:</Typography>
          <ul>
            <li><b>Buscar título:</b> digite parte do nome do livro para encontrar resultados rapidamente.</li>
            <li><b>Buscar por palavra-chave</b> pesquise por temas, habilidades ou assuntos presentes na descrição dos livros, como <i>disciplina</i>, <i>resiliência</i>, <i>oratória</i>, <i>persuasão</i>, etc.</li>
            <li><b>Filtros de Categoria e Autor:</b> refine sua busca selecionando uma categoria específica ou um autor de interesse.</li>
          </ul>
          <Typography variant="subtitle1" sx={{ fontWeight: 500, mt: 2, color: theme.palette.primary.main }}>Dicas para melhores resultados:</Typography>
          <ul>
            <li>Combine os campos de busca e filtros para encontrar livros ainda mais relevantes. Exemplo: busque por "liderança" na descrição e selecione a categoria "Negócios".</li>
            <li>Use palavras-chave amplas ou específicas para explorar diferentes temas.</li>
            <li>Limpe os filtros para ver todas as recomendações novamente.</li>
          </ul>
          <Typography sx={{ mt: 2, color: 'text.secondary' }}>
            Dúvidas ou sugestões? Fale com a equipe Habitop!
          </Typography>
        </DialogContent>
      </Dialog>
      {/* Botão para exibir/ocultar busca no mobile */}
      {isMobile && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Button
            onClick={() => setBuscaAberta(v => !v)}
            startIcon={buscaAberta ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            size="small"
            variant="outlined"
            sx={{ flex: 1, justifyContent: 'flex-start' }}
          >
            {buscaAberta ? 'Ocultar campos de busca' : 'Exibir campos de busca'}
          </Button>
          <IconButton onClick={() => setOpenAjuda(true)} color="primary" size="large" sx={{ ml: 1 }}>
            <HelpOutlineIcon />
          </IconButton>
        </Box>
      )}
      {/* Formulário de cadastro (apenas admin) */}
      {isAdmin && (
        <Box component="form" onSubmit={cadastrarLivro} sx={{ mb: 4, p: 2, border: '1px solid', borderColor: theme.palette.divider, borderRadius: 2, background: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fafafa', color: theme.palette.text.primary }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: theme.palette.text.primary }}>Cadastrar novo livro</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField label="Título" name="titulo" value={novoLivro.titulo} onChange={handleChange} fullWidth required InputLabelProps={{ style: { color: theme.palette.text.primary } }} InputProps={{ style: { color: theme.palette.text.primary } }} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                multiple
                freeSolo
                options={autoresUnicos}
                value={Array.isArray(novoLivro.autores) ? novoLivro.autores : []}
                onChange={(_, value) => setNovoLivro({ ...novoLivro, autores: value })}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Autores" placeholder="Adicionar autor" fullWidth InputLabelProps={{ style: { color: theme.palette.text.primary } }} InputProps={{ ...params.InputProps, style: { color: theme.palette.text.primary } }} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              <Autocomplete
                multiple
                freeSolo
                options={categorias}
                value={novoLivro.categorias || []}
                onChange={(_, value) => {
                  setNovoLivro({ ...novoLivro, categorias: value });
                  value.forEach(cat => { if (!categorias.includes(cat)) adicionarCategoria(cat); });
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Categorias" placeholder="Adicionar categoria" fullWidth InputLabelProps={{ style: { color: theme.palette.text.primary } }} InputProps={{ ...params.InputProps, style: { color: theme.palette.text.primary } }} />
                )}
                fullWidth
                sx={{ flex: 1, minWidth: 180 }}
              />
              <Button
                variant="outlined"
                size="small"
                sx={{ ml: 1, height: 40, mt: { xs: 1, sm: 0 } }}
                onClick={sugerirCategorias}
              >
                Sugerir Categorias
              </Button>
            </Grid>
            {categoriasSugeridas.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 1 }}>Categorias sugeridas:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {categoriasSugeridas.map(cat => (
                    <Chip
                      key={cat}
                      label={cat}
                      clickable
                      color="primary"
                      onClick={() => {
                        if (!novoLivro.categorias.includes(cat)) {
                          setNovoLivro({ ...novoLivro, categorias: [...novoLivro.categorias, cat] });
                        }
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField label="Descrição" name="descricao" value={novoLivro.descricao} onChange={handleChange} fullWidth multiline minRows={2} InputLabelProps={{ style: { color: theme.palette.text.primary } }} InputProps={{ style: { color: theme.palette.text.primary } }} />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField label="Link de compra (Amazon)" name="link" value={novoLivro.link} onChange={handleChange} fullWidth InputLabelProps={{ style: { color: theme.palette.text.primary } }} InputProps={{ style: { color: theme.palette.text.primary } }} />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField label="URL da imagem/capa" name="imagem" value={novoLivro.imagem} onChange={handleChange} fullWidth InputLabelProps={{ style: { color: theme.palette.text.primary } }} InputProps={{ style: { color: theme.palette.text.primary } }} />
            </Grid>
          </Grid>
          <Button type="submit" variant="contained" color="primary" sx={{ mt: 2 }} disabled={enviando}>
            {enviando ? 'Cadastrando...' : 'Cadastrar Livro'}
          </Button>
        </Box>
      )}

      {/* Título dos resultados da busca */}
      {buscou && (busca || buscaDescricao || filtroCategoria || filtroAutor) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ mt: 1, color: theme.palette.mode === 'dark' ? '#fff' : '#222' }}>
            Resultados da busca por:
            {busca && <span style={{ color: theme.palette.primary.main }}> título "{busca}"</span>}
            {buscaDescricao && <span style={{ color: theme.palette.primary.main }}>{busca ? ' e' : ''} palavra-chave "{buscaDescricao}"</span>}
            {filtroCategoria && <span style={{ color: theme.palette.primary.main }}>{(busca || buscaDescricao) ? ' e' : ''} categoria "{filtroCategoria}"</span>}
            {filtroAutor && <span style={{ color: theme.palette.primary.main }}>{(busca || buscaDescricao || filtroCategoria) ? ' e' : ''} autor "{filtroAutor}"</span>}
          </Typography>
          <Button onClick={limparFiltros} variant="outlined" color="secondary" size="small">
            Limpar Filtros
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="livros-por-pagina-label">Por página</InputLabel>
          <Select
            labelId="livros-por-pagina-label"
            value={livrosPorPagina}
            label="Por página"
            onChange={e => {
              setLivrosPorPagina(Number(e.target.value));
              setPagina(1);
            }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={-1}>Todos</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" sx={{ ml: 2 }}>
          Exibindo {mostrarTodos ? totalLivros : livrosPaginados.length} de {totalLivros} livros
        </Typography>
        {!mostrarTodos && totalPaginas > 1 && (
          <Pagination
            count={totalPaginas}
            page={pagina}
            onChange={(_, value) => setPagina(value)}
            color="primary"
            size="small"
            sx={{ ml: 2 }}
          />
        )}
      </Box>

      <Grid container spacing={3}>
        {livrosPaginados.map((livro) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={livro.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', cursor: editandoId ? 'default' : 'pointer' }}
              onClick={(e) => {
                // Só abre o modal se NÃO estiver editando e o alvo não for o botão de editar
                if (!editandoId && !(e.target.closest('button[data-edit-btn]'))) {
                  abrirDetalhesLivro(livro);
                }
              }}
            >
              {livro.imagem && (
                <CardMedia
                  component="img"
                  height="220"
                  image={livro.imagem}
                  alt={livro.titulo}
                  sx={{ objectFit: 'contain', p: 2 }}
                />
              )}
              <CardContent sx={{ flexGrow: 1 }}>
                {editandoId === livro.id ? (
                  <>
                    <TextField label="Título" name="titulo" value={editLivro.titulo} onChange={e => setEditLivro({ ...editLivro, titulo: e.target.value })} fullWidth sx={{ mb: 1 }} />
                    <Autocomplete
                      multiple
                      freeSolo
                      options={autoresUnicos}
                      value={Array.isArray(editLivro.autores) && editLivro.autores.length > 0 ? editLivro.autores : ['']}
                      onChange={(_, value) => setEditLivro({ ...editLivro, autores: value })}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Autores" placeholder="Adicionar autor" fullWidth sx={{ mb: 1 }} />
                      )}
                      sx={{ mb: 1 }}
                    />
                    <Autocomplete
                      multiple
                      freeSolo
                      options={categorias}
                      value={editLivro.categorias || []}
                      onChange={(_, value) => {
                        setEditLivro({ ...editLivro, categorias: value });
                        value.forEach(cat => { if (!categorias.includes(cat)) adicionarCategoria(cat); });
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Categorias" placeholder="Adicionar categoria" fullWidth sx={{ mb: 1 }} />
                      )}
                    />
                    <TextField label="Descrição" name="descricao" value={editLivro.descricao} onChange={e => setEditLivro({ ...editLivro, descricao: e.target.value })} fullWidth multiline minRows={2} sx={{ mb: 1 }} inputRef={descricaoEditRef} />
                    <TextField label="Link de compra (Amazon)" name="link" value={editLivro.link} onChange={e => setEditLivro({ ...editLivro, link: e.target.value })} fullWidth sx={{ mb: 1 }} />
                    <TextField label="URL da imagem/capa" name="imagem" value={editLivro.imagem} onChange={e => setEditLivro({ ...editLivro, imagem: e.target.value })} fullWidth sx={{ mb: 1 }} />
                  </>
                ) : (
                  <>
                    <Typography variant="subtitle1" fontWeight={500} sx={{ color: theme.palette.primary.main }}>{livro.titulo}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {(livro.autores || []).join(', ')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 1 }}>
                      {(livro.categorias || []).map(cat => (
                        <Chip key={cat} label={cat} size="small" color="primary" variant="outlined" />
                      ))}
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 8,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal',
                        minHeight: '8em'
                      }}
                    >
                      {livro.descricao}
                    </Typography>
                  </>
                )}
              </CardContent>
              {isAdmin && (
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: 1, mb: 2 }}>
                  {editandoId === livro.id ? (
                    <>
                      <Button onClick={salvarEdicao} variant="contained" color="primary" size="small">Salvar</Button>
                      <Button onClick={cancelarEdicao} variant="outlined" color="secondary" size="small">Cancelar</Button>
                    </>
                  ) : (
                    <>
                      <Button onClick={() => iniciarEdicao(livro)} variant="outlined" color="primary" size="small" data-edit-btn>Editar</Button>
                      <Button onClick={() => removerLivro(livro.id)} variant="outlined" color="error" size="small">Remover</Button>
                    </>
                  )}
                </Box>
              )}
              {livro.link && !editandoId && (
                <Button
                  href={livro.link}
                  target="_blank"
                  rel="noopener"
                  variant="contained"
                  color="primary"
                  sx={{ m: 2 }}
                >
                  Comprar
                </Button>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>
      {/* Paginação inferior */}
      {!mostrarTodos && totalPaginas > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
          <Pagination
            count={totalPaginas}
            page={pagina}
            onChange={(_, value) => setPagina(value)}
            color="primary"
            size="medium"
          />
        </Box>
      )}
      {!loading && livros.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Nenhum livro encontrado para sua busca.
        </Typography>
      )}
      {/* Modal de detalhes do livro */}
      <Dialog open={!!livroSelecionado} onClose={fecharDetalhesLivro} maxWidth="sm" fullWidth>
        <DialogTitle>Detalhes do Livro</DialogTitle>
        <DialogContent>
          {livroSelecionado && (
            <Box>
              {livroSelecionado.imagem && (
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <img src={livroSelecionado.imagem} alt={livroSelecionado.titulo} style={{ maxHeight: 200, maxWidth: '100%', objectFit: 'contain' }} />
                </Box>
              )}
              <Typography variant="h6" fontWeight={600} sx={{ color: theme.palette.primary.main }}>{livroSelecionado.titulo}</Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {(livroSelecionado.autores || []).join(', ')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                {(livroSelecionado.categorias || []).map(cat => (
                  <Chip key={cat} label={cat} size="small" color="primary" variant="outlined" />
                ))}
              </Box>
              <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                {livroSelecionado.descricao}
              </Typography>
              {livroSelecionado.link && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Button
                    href={livroSelecionado.link}
                    target="_blank"
                    rel="noopener"
                    variant="contained"
                    color="primary"
                  >
                    Comprar
                  </Button>
                </Box>
              )}
              {livrosRecomendados.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>
                    Você também pode gostar:
                  </Typography>
                  <Grid container spacing={2}>
                    {livrosRecomendados.map(livro => (
                      <Grid item xs={12} sm={6} md={3} key={livro.id}>
                        <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={() => { fecharDetalhesLivro(); abrirDetalhesLivro(livro); }}>
                          {livro.imagem && (
                            <CardMedia
                              component="img"
                              height="120"
                              image={livro.imagem}
                              alt={livro.titulo}
                              sx={{ objectFit: 'contain', p: 1 }}
                            />
                          )}
                          <CardContent sx={{ p: 1 }}>
                            <Typography variant="subtitle2" fontWeight={500} noWrap>{livro.titulo}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {(livro.autores || []).join(', ')}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDetalhesLivro} color="primary">Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 